import { isPlaceholderPlace } from './placeLabel';

/**
 * The town at a pair of coordinates, by name — "Penticton", never "Your location".
 *
 * Geolocation hands back numbers, and for a long while the app stored them with the label
 * "Your location". That label leaked everywhere a city name belongs: a signal's city, then a Town
 * Square named after it. This names the place instead (Mapbox reverse geocoding, the same token
 * the location picker uses), cached per ~1km so it's asked once.
 */
const STORAGE_KEY = 'pr.userLocation';
const cache = new Map();

export async function reverseGeocode(lat, lng) {
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  const key = `${lat.toFixed(2)},${lng.toFixed(2)}`;
  if (cache.has(key)) return cache.get(key);
  const token = process.env.REACT_APP_MAPBOX_TOKEN;
  if (!token) return null;
  try {
    const res = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?types=place,locality&limit=1&access_token=${token}`);
    if (!res.ok) return null;
    const json = await res.json();
    const name = json?.features?.[0]?.text || null;
    const label = name && !isPlaceholderPlace(name) ? name : null;
    cache.set(key, label);
    return label;
  } catch {
    return null;
  }
}

/** The saved location, its placeholder label replaced by the real town (written back). */
export async function repairStoredLocation() {
  let loc = null;
  try { loc = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch { return null; }
  if (!loc || typeof loc.lat !== 'number' || !isPlaceholderPlace(loc.label)) return loc;
  const label = await reverseGeocode(loc.lat, loc.lng);
  if (!label) return loc;
  const next = { ...loc, label };
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* storage off */ }
  return next;
}

/**
 * The other direction: a town's coordinates from its name, so a city picked by name can
 * narrow a location-aware fetch. The answer is remembered in `pr_location_coords`
 * (`services/userLocation`), so each town is asked for once, ever.
 *
 * Returns `{ lat, lng }` or null — a null is not a failure worth showing anyone: the city is
 * still set, the map-shaped fetches just stay as wide as they were.
 */
export async function geocodeCity(name) {
  const city = (name || '').trim();
  if (!city) return null;
  const { coordsFor, rememberCoords } = await import('../services/userLocation');
  const known = coordsFor(city);
  if (known) return known;
  const token = process.env.REACT_APP_MAPBOX_TOKEN;
  if (!token) return null;
  try {
    const res = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(city)}.json?types=place,locality&limit=1&access_token=${token}`);
    if (!res.ok) return null;
    const json = await res.json();
    const [lng, lat] = json?.features?.[0]?.center || [];
    if (typeof lat !== 'number' || typeof lng !== 'number') return null;
    rememberCoords(city, lat, lng);
    return { lat, lng };
  } catch {
    return null;
  }
}

/** Towns matching what is being typed, for the location panel's search field. */
export async function searchCities(query, { limit = 8 } = {}) {
  const q = (query || '').trim();
  const token = process.env.REACT_APP_MAPBOX_TOKEN;
  if (!q || !token) return [];
  try {
    const res = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?types=place,locality&limit=${limit}&language=en&access_token=${token}`);
    if (!res.ok) return [];
    const json = await res.json();
    return (json.features || []).map((f) => {
      const [lng, lat] = f.center || [];
      const region = (f.context || []).find((c) => (c.id || '').startsWith('region.'))?.text;
      return { name: f.text, region: region || null, lat, lng };
    }).filter((c) => c.name && typeof c.lat === 'number');
  } catch {
    return [];
  }
}
